import React, { useState, useEffect, useCallback, useContext } from 'react';
import Proveedor from './Proveedor';
import { AuthContext } from '../contexts/AuthContext';
import { apiFetch } from '../utils/apiFetch';

interface Provider 
{
  id: number;
  name: string;
  api_key: string;
  valid_ips: string;
}

const Proveedores: React.FC = () => 
{
  const { token } = useContext(AuthContext);
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);

  //-----------------------------------------------------------

  const fetchProviders = useCallback(async () =>
  {
    setLoading(true);
    setError(null);

    try 
    {
      const response = await apiFetch(`web/get_providers`, {
        method: 'GET'});

      if (response.ok) 
      {
        const data = await response.json();
        setProviders(data);
      } 
      else 
      {
        throw new Error('Error al cargar proveedores.');
      }
    } 
    catch (error) 
    {
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      setError(errorMsg);
    } 
    finally 
    {
      setLoading(false);
    }
  }, [token]);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    fetchProviders();
  }, [fetchProviders]);

  //-----------------------------------------------------------

  const handleCreateClick = () => 
  {
    setSelectedProvider({
        id: -1, // Para indicar que es un nuevo proveedor
        name: '',
        api_key: '',
        valid_ips: ''}); // Crear un proveedor vacío para la creación
  };
  
  //-----------------------------------------------------------

  const handleEditClick = (provider: Provider) => 
  {
    setSelectedProvider(provider); // Para editar el proveedor existente
  };

  //-----------------------------------------------------------

  const handleFormClose = () => 
  {
    setSelectedProvider(null); // Cerrar el formulario
    fetchProviders(); // Refrescar la lista de proveedores después de guardar o eliminar
  };

  //-----------------------------------------------------------

  return (
    <div className="eventos-container">
      {selectedProvider === null ? (
        <div>
          <div className="cod-toolbar" style={{ justifyContent: 'flex-end' }}>
              <button className="icon-btn icon-btn--create" onClick={handleCreateClick}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Crear proveedor
              </button>
          </div>
          {loading && <p>Buscando proveedores...</p>}
          {error && <p className="error-message">{error}</p>}
          {!loading && !error && providers.length === 0 && <p>No hay proveedores para mostrar.</p>}
          {!loading && !error && providers.length > 0 && (
            <table className="eventos-table">
              <tbody>
                {providers.map(provider => (
                  <tr key={provider.id}>
                    <td className="evento-name">{provider.name}</td>
                    <td className="api-key">{provider.api_key}</td>
                    <td className="evento-actions">
                      <button className="icon-btn icon-btn--edit" onClick={() => handleEditClick(provider)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="form-overlay">
          <Proveedor
            provider={selectedProvider} 
            onSave={handleFormClose}
            onCancel={handleFormClose}
            onDelete={(providerId) => {
              setProviders(providers.filter(provider => provider.id !== providerId));
              handleFormClose();
            }}
          />
        </div>
      )}
    </div>
  );
};  

export default Proveedores;
